import styled from "styled-components";
import {PanelButton} from "games/GameFrame.styles";
import {giftSurface} from "peel/PeelTag.styles";
import {fontFamilies} from "theme/tokens";

/**
 * Externalized styles for the recipient arcade's game column and finish screen
 * (kitecorp/transferify-ui#2889), after the approved prototype. Every colour reads `theme.tokens`.
 */

/** The open game, in the editorial column beside the file card: never over it. */
export const ArcadeGameColumn = styled.div`
	position: relative;
	z-index: 1;
	/*
	 * The landing content it sits in passes pointers through to the art behind it (pointer-events: none),
	 * so the game takes them back: without this a tap on the canvas or on the × reached the campaign art
	 * or the sender's wallpaper instead (kitecorp/transferify-ui#2990).
	 */
	pointer-events: auto;
	/* The finish screen's three buttons must fit inside Byte Runner's canvas, the shortest, on a phone. */
	--game-overlay-padding: 8px;
	display: grid;
	align-content: center;
	width: 100%;
	/* Narrow on a wide window, so the sender's art beside it stays in view. */
	max-width: 460px;
	min-width: 0;
	height: 100%;
`;

/** The finish screen's buttons: the main one and the quiet one side by side, wrapping on a phone. */
export const FinishRow = styled.div`
	display: flex;
	flex-wrap: wrap;
	justify-content: center;
	gap: 8px;
	margin-top: 6px;

	/* Compact to fit the shortest canvas; each keeps a 44px touch target around its visible face. */
	${PanelButton} {
		position: relative;
		margin-top: 0;
		min-height: 36px;
		padding: 6px 14px;

		&::before {
			content: "";
			position: absolute;
			inset-inline: 0;
			top: 50%;
			height: 44px;
			transform: translateY(-50%);
		}
	}
`;

export const QuietButton = styled(PanelButton)`
	background: ${({theme}) => theme.tokens.field};
	color: ${({theme}) => theme.tokens.ink};

	&:hover {
		background: ${({theme}) => theme.tokens.fieldLine};
	}
`;

/** The friend's gift wears the gift tag's own surface, so it reads as the same gift the card carries. */
export const GiftButton = styled(PanelButton)`
	${giftSurface}

	&:hover {
		background: ${({theme}) => theme.tokens.goldDark};
	}
`;

/** "Keep playing": text-sized to fit the shortest game's canvas, with a 44px touch target around it. */
export const KeepPlayingButton = styled.button`
	position: relative;
	margin-top: 4px;
	padding: 2px 6px;
	border: 0;
	background: transparent;
	color: ${({theme}) => theme.tokens.ink2};
	font: 600 13px ${fontFamilies.sans};
	cursor: pointer;

	&::before {
		content: "";
		position: absolute;
		inset-inline: 0;
		top: 50%;
		height: 44px;
		transform: translateY(-50%);
	}

	&:hover {
		color: ${({theme}) => theme.tokens.ink};
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.focusOutline};
		outline-offset: 2px;
	}
`;

/**
 * Holds the gift's popover in the React tree without a box of its own. The popover is portalled out of
 * the game, but React still bubbles its presses through here, and the game's overlay would read a press
 * inside the gift panel as a press on the game.
 */
export const PopoverHost = styled.div`
	display: contents;
`;
